import { useCallback, useEffect, useRef, useState } from "react";
import { ReleaseVersionChecker } from "@app/lib/release-version";

export function useReleaseUpdate(routeKey: string): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const checkerRef = useRef<ReleaseVersionChecker | null>(null);

  if (!checkerRef.current) {
    checkerRef.current = new ReleaseVersionChecker({
      currentVersion: import.meta.env.VITE_BUILD_VERSION?.trim() ?? "",
    });
  }

  const checkForUpdate = useCallback(() => {
    if (updateAvailable) {
      return;
    }

    void checkerRef.current
      ?.checkForUpdate()
      .then(setUpdateAvailable)
      .catch((error) => {
        console.warn("[release] Failed to check for an update", error);
      });
  }, [updateAvailable]);

  useEffect(() => {
    checkForUpdate();
  }, [checkForUpdate, routeKey]);

  useEffect(() => {
    const handleFocus = () => checkForUpdate();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return updateAvailable;
}
